// sapi-admin/src/pages/PiSellRequestsManager.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, updateDoc, doc, orderBy, onSnapshot } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, Clock, Send, Phone, RefreshCw, 
  Filter, ChevronDown, X, Eye, Users, TrendingUp, Search, AlertTriangle,
  Pi
} from 'lucide-react';
import { db } from '../firebase/config';
import FormatPrice from '../components/FormatPrice';

// ─── Composants UI ──────────────────────────────────────────────────────────
const GlassPanel = ({ children, style = {} }) => (
  <div style={{ background: "rgba(17,24,39,.85)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 16, backdropFilter: "blur(16px)", ...style }}>{children}</div>
);

const MiniStat = ({ label, value, accent = "#10b981", icon: Icon, index = 0 }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * .07 }}
    style={{ background: "rgba(17,24,39,.85)", border: `1px solid ${accent}33`, borderRadius: 14, padding: "14px 16px", position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", top: -24, right: -24, width: 72, height: 72, borderRadius: "50%", background: `${accent}18`, filter: "blur(20px)" }} />
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div>
        <p style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".14em", color: "#475569", marginBottom: 8 }}>{label}</p>
        <p style={{ fontSize: 22, fontWeight: 700, color: "#fff", lineHeight: 1 }}>{value}</p>
      </div>
      {Icon && (<div style={{ width: 32, height: 32, borderRadius: 9, background: `${accent}18`, border: `1px solid ${accent}33`, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={15} color={accent} strokeWidth={1.8} /></div>)}
    </div>
    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: .5, delay: .2 + index * .07 }} style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: accent, opacity: .5, transformOrigin: "left" }} />
  </motion.div>
);

const Btn = ({ children, onClick, variant = 'ghost', size = 'sm', disabled = false, style = {} }) => {
  const V = { 
    ghost: { background: 'rgba(255,255,255,.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,.08)' },
    green: { background: 'rgba(16,185,129,.15)', color: '#34d399', border: '1px solid rgba(16,185,129,.3)' },
    red: { background: 'rgba(239,68,68,.15)', color: '#f87171', border: '1px solid rgba(239,68,68,.3)' },
    amber: { background: 'rgba(245,158,11,.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,.3)' },
  };
  const v = V[variant] || V.ghost;
  const pad = size === 'xs' ? '3px 8px' : size === 'sm' ? '5px 12px' : '8px 18px';
  return <button onClick={onClick} disabled={disabled} style={{ ...v, padding: pad, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .45 : 1, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'opacity .15s', whiteSpace: 'nowrap', ...style }}>{children}</button>;
};

const StatusBadge = ({ paymentStatus, status }) => {
  if (status === 'cancelled' || paymentStatus === 'cancelled') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">✗ Cancelled</span>;
  }
  if (paymentStatus === 'completed') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">✓ Paid</span>;
  }
  if (paymentStatus === 'pending_manual') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">⏳ Pending Payment</span>;
  }
  if (paymentStatus === 'pending') {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">⏳ Pending Pi</span>;
  }
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{paymentStatus || status}</span>;
};

// ═══════════════════════════════════════════════════════════════════════════
export default function PiSellManager() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ─── ÉCOUTE EN TEMPS RÉEL AVEC onSnapshot ──────────────────────────────
  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'piSellRequests'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const txList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTransactions(txList);
        setLoading(false);
      },
      (error) => {
        console.error('❌ Real-time listener error:', error);
        toast.error('Error loading transactions');
        setLoading(false);
      }
    );

    // Nettoyer l'écouteur au démontage
    return () => unsubscribe();
  }, []);

  // ─── Marquer comme payé ──────────────────────────────────────────────────
  const markAsPaid = async (transaction) => {
    if (transaction.paymentStatus !== 'pending_manual') {
      toast.error(`Cette transaction n'est pas en attente de paiement (statut: ${transaction.paymentStatus})`);
      return;
    }
    
    setProcessingId(transaction.id);
    try {
      await updateDoc(doc(db, 'piSellRequests', transaction.id), {
        paymentStatus: 'completed',
        status: 'completed',
        paymentCompletedAt: new Date().toISOString(),
        adminNote: 'Payment sent to user'
      });
      toast.success(`✅ Paiement marqué comme envoyé à ${transaction.phoneNumber}`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setProcessingId(null);
    }
  };

  // ─── Statistiques en temps réel ──────────────────────────────────────────
  const stats = useMemo(() => {
    const cancelled = transactions.filter(tx => tx.status === 'cancelled' || tx.paymentStatus === 'cancelled').length;
    const pendingManual = transactions.filter(tx => tx.paymentStatus === 'pending_manual').length;
    const completed = transactions.filter(tx => tx.paymentStatus === 'completed').length;
    const totalPaid = transactions
      .filter(tx => tx.paymentStatus === 'completed')
      .reduce((sum, tx) => sum + (tx.fcfaAmount || 0), 0);
    return { total: transactions.length, pendingManual, completed, cancelled, totalPaid };
  }, [transactions]);

  // ─── Filtrage ────────────────────────────────────────────────────────────
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(tx => 
        tx.phoneNumber?.toLowerCase().includes(term) ||
        tx.userId?.toLowerCase().includes(term) ||
        tx.id?.toLowerCase().includes(term)
      );
    }
    
    if (filterStatus !== 'all') {
      if (filterStatus === 'pending') {
        filtered = filtered.filter(tx => tx.paymentStatus === 'pending_manual');
      } else if (filterStatus === 'completed') {
        filtered = filtered.filter(tx => tx.paymentStatus === 'completed');
      } else if (filterStatus === 'cancelled') {
        filtered = filtered.filter(tx => tx.status === 'cancelled' || tx.paymentStatus === 'cancelled');
      }
    }
    
    return filtered;
  }, [transactions, searchTerm, filterStatus]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const COL = "100px 1fr 120px 100px 100px";
  const hasFilters = filterStatus !== 'all' || searchTerm;

  // ─── Chargement initial ──────────────────────────────────────────────────
  if (loading && transactions.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(249,115,22,.3)', borderTopColor: '#f97316', animation: 'spin .8s linear infinite' }} />
        <p style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading PiSellRequests…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ paddingBottom: 48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.18em', color: 'orange', marginBottom: 4 }}>Pi Exchange</p>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
              <Pi size={20} color="orange" strokeWidth={2} /> Pi Sell Requests
            </h1>
            <p style={{ fontSize: 11, color: '#475569' }}>Manage customer requests to sell Pi for FCFA</p>
          </div>
          {/* ✅ Bouton Refresh gardé pour un rafraîchissement manuel si nécessaire */}
          <Btn variant="ghost" onClick={() => {}} disabled={loading}>
            <RefreshCw size={13} style={{ animation: loading ? 'spin .8s linear infinite' : 'none' }} />
            {loading ? 'Loading…' : 'Live ✅'}
          </Btn>
        </div>

        {/* Stats en temps réel */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
          <MiniStat label="Total Requests" value={stats.total} accent="#f59e0b" icon={Users} index={0} />
          <MiniStat label="Pending Payment" value={stats.pendingManual} accent="#fbbf24" icon={Clock} index={1} />
          <MiniStat label="Completed" value={stats.completed} accent="#34d399" icon={CheckCircle} index={2} />
          <MiniStat label="Cancelled" value={stats.cancelled} accent="#f87171" icon={AlertTriangle} index={3} />
          <MiniStat label="Total Paid (FCFA)" value={`${stats.totalPaid.toLocaleString()}`} accent="#10b981" icon={TrendingUp} index={4} />
        </div>
      </div>

      {/* Filters - inchangé */}
      <GlassPanel style={{ marginBottom: 14 }}>
        <button onClick={() => setFiltersOpen(v => !v)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', color: '#e2e8f0' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600 }}><Filter size={13} color="#10b981" /> Filters &amp; Search {hasFilters && <span style={{ background: 'rgba(16,185,129,.2)', color: '#34d399', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 20, border: '1px solid rgba(16,185,129,.3)' }}>ACTIVE</span>}</span>
          <ChevronDown size={13} style={{ transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s', color: '#475569' }} />
        </button>
        <AnimatePresence>
          {filtersOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: .2 }} style={{ overflow: 'hidden' }}>
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(255,255,255,.05)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginTop: 14 }}>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 6 }}>Search</label>
                    <div style={{ position: 'relative' }}>
                      <Search size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#475569' }} />
                      <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Phone, user ID…" style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '8px 12px 8px 28px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none' }} />
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 6 }}>Status</label>
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ background: '#111827', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '8px 12px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none' }}>
                      <option value="all">All</option>
                      <option value="pending">Pending Payment</option>
                      <option value="completed">Completed (Paid)</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                {hasFilters && <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}><Btn variant="red" size="xs" onClick={() => { setSearchTerm(''); setFilterStatus('all'); }}><X size={11} /> Clear</Btn></div>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </GlassPanel>

      {/* Table */}
      <GlassPanel>
        <div style={{ display: 'grid', gridTemplateColumns: COL, gap: 8, padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,.05)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#334155', fontWeight: 700 }}>
          <div>Status</div><div>Customer / Phone</div><div>Amount</div><div>Date</div><div>Actions</div>
        </div>

        {filteredTransactions.length === 0 && !loading && (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <Pi size={32} style={{ margin: '0 auto 12px', opacity: .2, color: '#5e6b7e' }} />
            <p style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>No sell requests found</p>
          </div>
        )}

        {filteredTransactions.map((tx, idx) => {
          const isCancelled = tx.status === 'cancelled' || tx.paymentStatus === 'cancelled';
          const showMarkPaid = !isCancelled && tx.paymentStatus === 'pending_manual';
          
          return (
            <motion.div key={tx.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(idx * .02, .3) }}
              style={{ display: 'grid', gridTemplateColumns: COL, gap: 8, padding: '11px 16px', borderBottom: '1px solid rgba(255,255,255,.03)', alignItems: 'center', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(16,185,129,.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              onClick={() => { setSelectedTx(tx); setShowModal(true); }}>
              <div><StatusBadge paymentStatus={tx.paymentStatus} status={tx.status} /></div>
              <div><p style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0' }}>{tx.phoneNumber}</p><p style={{ fontSize: 9, color: '#475569' }}>{tx.operator}</p></div>
              <div><p style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>{FormatPrice(tx.piAmount)}</p><p style={{ fontSize: 10, color: '#475569' }}>{tx.fcfaAmount?.toLocaleString()} FCFA</p></div>
              <div><p style={{ fontSize: 11, color: '#64748b' }}>{formatDate(tx.createdAt)}</p></div>
              <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                {showMarkPaid && (
                  <Btn size="xs" variant="green" onClick={() => markAsPaid(tx)} disabled={processingId === tx.id}>
                    {processingId === tx.id ? <div style={{ width: 12, height: 12, border: '2px solid #34d399 border-top-color:transparent rounded-full animate-spin' }} /> : <Send size={10} />}
                    Mark Paid
                  </Btn>
                )}
                <Btn size="xs" variant="ghost" onClick={() => { setSelectedTx(tx); setShowModal(true); }}><Eye size={10} /></Btn>
                <a href={`tel:${tx.phoneNumber}`} style={{ textDecoration: 'none' }}><Btn size="xs" variant="amber"><Phone size={10} /></Btn></a>
              </div>
            </motion.div>
          );
        })}

        {filteredTransactions.length > 0 && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,.04)' }}>
            <p style={{ fontSize: 10, color: '#334155' }}>Showing <span style={{ color: '#10b981', fontWeight: 700 }}>{filteredTransactions.length}</span> of <span style={{ color: '#10b981', fontWeight: 700 }}>{transactions.length}</span> requests</p>
          </div>
        )}
      </GlassPanel>

      {/* Modal Détails - inchangé */}
      <AnimatePresence>
        {showModal && selectedTx && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}
            onClick={() => setShowModal(false)}>
            <motion.div initial={{ scale: .95 }} animate={{ scale: 1 }} exit={{ scale: .95 }}
              style={{ background: '#0d1526', border: '1px solid rgba(255,255,255,.1)', borderRadius: 20, maxWidth: 500, width: '100%', maxHeight: '90vh', overflow: 'auto' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Sell Request Details</h3>
                <button onClick={() => setShowModal(false)} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: 5, cursor: 'pointer' }}><X size={14} color="#94a3b8" /></button>
              </div>
              <div style={{ padding: 20 }}>
                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                  <StatusBadge paymentStatus={selectedTx.paymentStatus} status={selectedTx.status} />
                </div>
                <div style={{ display: 'grid', gap: 12 }}>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Request ID</label><p style={{ fontSize: 12, color: '#e2e8f0', fontFamily: 'monospace' }}>{selectedTx.id}</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Operator</label><p style={{ fontSize: 12, color: '#e2e8f0', fontFamily: 'monospace' }}>{selectedTx.operator}</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Phone Number</label><p style={{ fontSize: 14, fontWeight: 700, color: '#f59e0b' }}>{selectedTx.phoneNumber}</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>User ID</label><p style={{ fontSize: 12, color: '#94a3b8' }}>{selectedTx.userId}</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Amount</label><p style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{FormatPrice(selectedTx.piAmount)}</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>FCFA Amount</label><p style={{ fontSize: 16, fontWeight: 600, color: '#34d399' }}>{selectedTx.fcfaAmount?.toLocaleString()} FCFA</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Rate</label><p style={{ fontSize: 12, color: '#94a3b8' }}>{selectedTx.rate} FCFA/π</p></div>
                  <div><label style={{ fontSize: 10, color: '#475569' }}>Date</label><p style={{ fontSize: 12, color: '#94a3b8' }}>{formatDate(selectedTx.createdAt)}</p></div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info box */}
      <div style={{ marginTop: 16, background: 'rgba(16,185,129,.08)', border: '1px solid rgba(16,185,129,.2)', borderRadius: 12, padding: '12px 16px' }}>
        <p style={{ fontSize: 11, color: '#34d399', fontWeight: 600, marginBottom: 4 }}>💡 How it works</p>
        <ol style={{ fontSize: 10, color: '#64748b', marginLeft: 20, lineHeight: 1.6 }}>
          <li>User submits sell order → Pi transfer confirmed automatically</li>
          <li>Request appears here as "Pending Payment"</li>
          <li>You send FCFA to the user's Mobile Money</li>
          <li>Click "Mark Paid" to complete the transaction</li>
        </ol>
      </div>
    </motion.div>
  );
}